import api from '@shared/services/api';
import { QuestionBankItem, QuestionBankItemStatus } from '../types/question-bank.types';

export class QuestionBankEditorService {
  /**
   * Get a question bank item for editing
   * @param itemId ID of the item to edit
   * @returns The question bank item with all related data
   */
  static async getQuestionBankItemForEditing(
    itemId: string
  ): Promise<QuestionBankItem> {
    const response = await api.get(`/question-bank/items/${itemId}`);
    return response.data.item;
  }

  /**
   * Save a question bank item
   * @param itemId ID of the item to save
   * @param itemData Updated item data
   * @returns The saved question bank item
   */
  static async saveQuestionBankItem(
    itemId: string,
    itemData: Partial<QuestionBankItem>
  ): Promise<QuestionBankItem> {
    const response = await api.put(`/question-bank/items/${itemId}`, itemData);
    return response.data.item;
  }

  /**
   * Create a new question bank item
   * @param itemData New item data
   * @returns The created question bank item
   */
  static async createQuestionBankItem(
    itemData: Partial<QuestionBankItem>
  ): Promise<QuestionBankItem> {
    const response = await api.post('/question-bank/items', itemData);
    return response.data.item;
  }

  /**
   * Publish a question bank item
   * @param itemId ID of the item to publish
   * @returns The published question bank item
   */
  static async publishQuestionBankItem(
    itemId: string
  ): Promise<QuestionBankItem> {
    const response = await api.post(`/question-bank/items/${itemId}/publish`);
    return response.data.item;
  }

  /**
   * Unpublish a question bank item
   * @param itemId ID of the item to unpublish
   * @returns The unpublished question bank item
   */
  static async unpublishQuestionBankItem(
    itemId: string
  ): Promise<QuestionBankItem> {
    const response = await api.post(`/question-bank/items/${itemId}/unpublish`);
    return response.data.item;
  }

  /**
   * Delete a question bank item
   * @param itemId ID of the item to delete
   * @returns Success status
   */
  static async deleteQuestionBankItem(itemId: string): Promise<boolean> {
    const response = await api.delete(`/question-bank/items/${itemId}`);
    return response.status === 200;
  }

  /**
   * Get all categories for selection
   * @returns List of categories
   */
  static async getAllCategories() {
    const response = await api.get('/question-bank/categories');
    return response.data.categories;
  }
}
